import { format, subMonths } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useClient } from 'urql';

import {
  type CustomRoleFilter,
  Dimension1,
  POSTING_GET_DATA,
  POSTING_SOURCE_IDS,
} from '@revelio/data-access';

import type { Competitor } from './useCompetitors';
import type { ResolvedProfileIds } from './useResolveProfileIds';

/** Build a trailing 6-month date window for posting queries. */
const getDefaultPostingDateRange = (): {
  start_date: string;
  end_date: string;
} => {
  const now = new Date();
  return {
    start_date: format(subMonths(now, 6), 'yyyy-MM-dd'),
    end_date: format(now, 'yyyy-MM-dd'),
  };
};

export interface CompanyPostings {
  companyId: number;
  shortName: string;
  activePostings: number | null;
  expectedHires: number | null;
  timeToFill: number | null;
  postingSalary: number | null;
}

export interface PostingRoleFilters {
  custom_role?: CustomRoleFilter;
  seniority?: number;
}

interface UsePostingsComparisonResult {
  postings: CompanyPostings[];
  isLoading: boolean;
  error: string | null;
}

/** Maximum entities per API request */
const BATCH_SIZE = 10;

/** Split an array into chunks of a given size */
const chunk = <T>(arr: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

const toNumber = (val: number | null | undefined): number | null => val ?? null;

/**
 * Parse a single posting API response entry into CompanyPostings.
 */
export const parsePostingEntry = (entry: {
  metadata?: { id?: number | string | null; shortName?: string | null } | null;
  category?: ReadonlyArray<{
    metrics?: {
      active?: number | null;
      expected_hires?: number | null;
      time_to_fill?: number | null;
      salary?: number | null;
    } | null;
  } | null> | null;
}): CompanyPostings => {
  const id = entry.metadata?.id;
  const categories = entry.category;
  // The most recent time bucket (last category entry) has current metrics
  const latestCat =
    categories && categories.length > 0
      ? categories[categories.length - 1]
      : null;

  const metrics = latestCat?.metrics;

  return {
    companyId:
      typeof id === 'string' ? parseInt(id, 10) : ((id as number) ?? 0),
    shortName: entry.metadata?.shortName ?? '',
    activePostings: toNumber(metrics?.active),
    expectedHires: toNumber(metrics?.expected_hires),
    timeToFill: toNumber(metrics?.time_to_fill),
    postingSalary: toNumber(metrics?.salary),
  };
};

export const usePostingsComparison = (
  competitors: Competitor[],
  resolvedIds: ResolvedProfileIds,
  additionalCompanyIds?: number[],
  roleFilters?: PostingRoleFilters
): UsePostingsComparisonResult => {
  const gqlClient = useClient();
  const [postings, setPostings] = useState<CompanyPostings[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allCompanyIds = useMemo(() => {
    const ids = competitors.map((c) => c.id);
    if (resolvedIds.companyRcid && !ids.includes(resolvedIds.companyRcid)) {
      ids.unshift(resolvedIds.companyRcid);
    }
    for (const id of additionalCompanyIds ?? []) {
      if (!ids.includes(id)) {
        ids.push(id);
      }
    }
    return ids;
  }, [competitors, resolvedIds.companyRcid, additionalCompanyIds]);

  // Stable key for the effect dependency
  const idsKey = allCompanyIds.join(',');

  // Build a stable key for role filters so the effect re-runs when they change
  const roleFiltersKey = [
    roleFilters?.custom_role?.entities
      ?.map((e) => `${e?.levelId}-${e?.id}`)
      .join(';') ?? '',
    roleFilters?.seniority ?? '',
  ].join(',');

  // Track whether the component is still mounted
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Keep latest values in refs so the async callback always uses current data
  const roleFiltersRef = useRef(roleFilters);
  roleFiltersRef.current = roleFilters;
  const companyIdsRef = useRef(allCompanyIds);
  companyIdsRef.current = allCompanyIds;

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const ids = companyIdsRef.current;
    if (ids.length === 0) {
      setPostings([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const run = async () => {
      try {
        const batches = chunk(ids, BATCH_SIZE);
        const dateRange = getDefaultPostingDateRange();
        const currentRoleFilters = roleFiltersRef.current;

        const results = await Promise.all(
          batches.map((batch) => {
            const filters: Record<string, unknown> = {
              company: batch,
              provider: [POSTING_SOURCE_IDS.unified],
              metric_mode: 'posting',
              start_date: dateRange.start_date,
              end_date: dateRange.end_date,
            };
            if (currentRoleFilters?.custom_role) {
              filters.custom_role = currentRoleFilters.custom_role;
            }
            if (currentRoleFilters?.seniority) {
              filters.seniority = [currentRoleFilters.seniority];
            }
            return gqlClient
              .query(POSTING_GET_DATA, {
                filters,
                dim1: Dimension1.Company,
              })
              .toPromise();
          })
        );

        if (cancelled || !mountedRef.current) return;

        const merged: CompanyPostings[] = [];
        for (const result of results) {
          if (result.error) {
            setError('Failed to load postings data');
            continue;
          }
          const raw = result.data?.posting;
          if (!raw || !Array.isArray(raw)) continue;

          for (const entry of raw) {
            if (entry) {
              merged.push(parsePostingEntry(entry));
            }
          }
        }

        setPostings(merged);
      } catch {
        if (!cancelled && mountedRef.current) {
          setError('Failed to load postings data');
        }
      } finally {
        if (!cancelled && mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [idsKey, roleFiltersKey, gqlClient]); // eslint-disable-line react-hooks/exhaustive-deps

  return { postings, isLoading, error };
};
